import { LoadingOutlined } from "@ant-design/icons";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { searchPageTerm, setSearchTermInput } from "store/search/search.slice";

import { CustomDropdown, PaginationComponent } from "components";
import CategoryCard from "components/card/CategoryCard";
import { useTranslation } from "react-i18next";
import { ArticleSortOrder, sortOrderItems } from "types/articles/SortArticle";
import { parseNumberParam } from "utils/helpers/parseParam";
import ArticlesList from "../articlesCategories/articlesPage/ArticlesList";
import "./SearchPage.scss";

const SearchPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const searchTerm = useMemo(() => searchParams.get("term"), [searchParams]);
  const currentPage = useMemo(
    () => parseNumberParam(searchParams, "page", 1),
    [searchParams]
  );
  const pageSize = useMemo(
    () => parseNumberParam(searchParams, "take", 12),
    [searchParams]
  );
  const handlePageSizeChange = (take: string) => {
    const p = new URLSearchParams(searchParams);
    p.set("take", String(take));
    p.set("page", "1"); // resetujemo na prvu stranicu
    setSearchParams(p, { replace: true });
    window.scrollTo(0, 0);
  };
  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", String(newPage));
    setSearchParams(params, { replace: true });
    window.scrollTo(0, 0);
  };

  const sortOrder = useMemo(
    () => searchParams.get("sort") ?? ArticleSortOrder.Default,
    [searchParams]
  );
  const handleSortSelect = (key: string) => {
    const p = new URLSearchParams(searchParams);
    if (key === ArticleSortOrder.Default) {
      p.delete("sort");
    } else {
      p.set("sort", key);
    }
    p.set("page", "1");
    setSearchParams(p, { replace: true });
    window.scrollTo(0, 0);
  };

  const dispatch: any = useDispatch();
  const { is_loading, is_fetch, error, data, meta } = useSelector(
    (store: any) => store.search.searchPage
  );

  useEffect(() => {
    if (searchTerm) {
      dispatch(setSearchTermInput(searchTerm));
      dispatch(
        searchPageTerm({
          searchTerm: searchTerm,
          maxPrice: undefined,
          minPrice: undefined,
          order: sortOrder,
          take: pageSize,
          page: currentPage,
        })
      );
    } else {
      navigate("/");
    }
  }, [dispatch, navigate, searchTerm, location]);

  if (is_loading) {
    return <LoadingOutlined />;
  }
  if (error) {
    return <div>postoji greska</div>;
  }

  return (
    <div className="Page">
      <div className="Page-Content">
        {is_fetch && (
          <>
            <label>Termin pretrage: "{searchTerm}"</label>
            {data.article_categories.length !== 0 ? (
              <>
                <h2>Pretražene kategorije</h2>
                <div className="Search-Categories">
                  {data.article_categories.map((itemData: any, key: any) => (
                    <div key={key} className="Search-Categories-Item">
                      <CategoryCard
                        data={itemData}
                        key={key}
                        // action={hideDropdown}
                      />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div>
                <h2>Pretražene kategorije</h2>
                <label>Nema pretraženih kategorija</label>
              </div>
            )}
            <br />
            {data.articles.length !== 0 ? (
              <div className="Search-Page-Articles">
                <h2>Pretraženi proizvodi</h2>

                <div className="Sort-Dropdown">
                  <CustomDropdown
                    items={sortOrderItems}
                    selectedKey={sortOrder}
                    onChange={handleSortSelect}
                    placeholder={t("Sortiranje")}
                  />
                </div>

                <ArticlesList products={data.articles} productsLoaded={true} />
                <PaginationComponent
                  current={currentPage}
                  totalItems={meta.item_count}
                  pageSize={pageSize}
                  hasNextPage={meta.has_next_page}
                  changePageSize={handlePageSizeChange}
                  changePage={handlePageChange}
                />
              </div>
            ) : (
              <div>
                <h2>Pretraženi proizvodi</h2>
                <label>Nema pretraženih proizvoda</label>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default SearchPage;
